import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Plus,
  Copy,
  X,
  DollarSign,
  Search,
  Printer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LeadFilters, type FilterType } from '../components/LeadFilters';
import { LeadForm } from '../components/LeadForm';
import { AssignLeadModal } from '../components/AssignLeadModal';
import { LeadNotesSection } from '../components/LeadNotesSection';
import { CloseLeadDialog, type CloseType } from '../components/CloseLeadDialog';
import { PrintLeadSheetButton } from '../components/PrintLeadSheetButton';
import { LeadJobsPanel } from '@/features/jobs';
import { useJobsByLead } from '@/features/jobs/hooks/useJobsByLead';
import { useLeads } from '../hooks/useLeads';
import { useReferenceData } from '../hooks/useReferenceData';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { getLead, transitionLeadStatus } from '../api/leads-api';
import type { Lead, LeadsQueryParams } from '../types';

export function LeadsPage() {
  const navigate = useNavigate();
  const { id: urlLeadId } = useParams<{ id: string }>();
  const [queryParams, setQueryParams] = useState<LeadsQueryParams>({
    status: undefined,
    assignedToUserId: undefined,
  });
  const [currentFilter, setCurrentFilter] = useState<FilterType>('all-open');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [selectedCloseType, setSelectedCloseType] = useState<CloseType>('NoGo');
  const [isClosingLead, setIsClosingLead] = useState(false);

  // Initialize selectedLeadId from URL param if present
  const initialLeadId = urlLeadId ? parseInt(urlLeadId, 10) : undefined;
  const [selectedLeadId, setSelectedLeadId] = useState<number | undefined>(
    initialLeadId && !isNaN(initialLeadId) ? initialLeadId : undefined
  );

  // Track if we've loaded the initial URL lead
  const hasLoadedUrlLead = useRef(false);

  const { data: leads, isLoading, error, refetch } = useLeads(queryParams);
  const { users } = useReferenceData();
  const { data: jobs } = useJobsByLead(selectedLead?.id);
  const { hasPermission } = useAuth();

  // Permission checks
  const canCreateLeads = hasPermission(Permissions.LeadsCreate);
  const canUpdateLeads = hasPermission(Permissions.LeadsUpdate);
  // Note: canAssignLeads could be used to conditionally show assign functionality
  // const canAssignLeads = hasPermission(Permissions.LeadsAssign);

  const loadLead = useCallback(async (leadId: number) => {
    try {
      const fullLead = await getLead(leadId);
      setSelectedLead(fullLead);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to load lead details');
    }
  }, []);

  // Load lead from URL param if provided (only on initial mount)
  useEffect(() => {
    if (urlLeadId && !hasLoadedUrlLead.current) {
      const leadId = parseInt(urlLeadId, 10);
      if (!isNaN(leadId)) {
        hasLoadedUrlLead.current = true;
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => loadLead(leadId), 0);
      }
    }
  }, [urlLeadId, loadLead]);

  const handleLeadSelect = (leadId: number | undefined) => {
    setSelectedLeadId(leadId);
    if (leadId) {
      loadLead(leadId);
    } else {
      setSelectedLead(null);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setCurrentFilter(filter);

    // Update query params based on filter
    switch (filter) {
      case 'all-open':
        setQueryParams({
          status: undefined,
          assignedToUserId: undefined,
        });
        break;
      case 'unassigned':
        setQueryParams({
          status: 'Unassigned',
          assignedToUserId: undefined,
        });
        break;
      case 'recent-no-gos':
        setQueryParams({
          status: 'NoGo',
          assignedToUserId: undefined,
        });
        break;
      case 'recently-closed':
        setQueryParams({
          status: 'Closed',
          assignedToUserId: undefined,
        });
        break;
      case 'by-estimator':
        // Already handled by onAssignedToChange
        break;
    }
  };

  const handleAssignedToChange = (userId: number | undefined) => {
    setQueryParams((prev) => ({
      ...prev,
      assignedToUserId: userId,
    }));
  };

  const handleNewLead = () => {
    handleLeadSelect(undefined);
  };

  const handleCopyLead = () => {
    if (!selectedLead) {
      alert('Please select a lead first');
      return;
    }
    // Create a copy without the id - this will create a new lead
    const leadCopy = { ...selectedLead, id: 0 } as Lead;
    setSelectedLeadId(undefined);
    setSelectedLead(leadCopy);
  };

  const handleFormSuccess = (lead: Lead) => {
    refetch();
    // If this was a new lead, select it
    if (lead.id) {
      handleLeadSelect(lead.id);
    }
  };

  const handleFormCancel = () => {
    // If we were editing, reload the lead; if new, clear selection
    if (selectedLeadId) {
      loadLead(selectedLeadId);
    } else {
      setSelectedLead(null);
    }
  };

  const handleAssignSuccess = () => {
    refetch();
    if (selectedLeadId) {
      loadLead(selectedLeadId);
    }
  };

  const handleCloseClick = () => {
    if (!selectedLead) {
      alert('Please select a lead first');
      return;
    }
    setSelectedCloseType('NoGo');
    setIsCloseDialogOpen(true);
  };

  const handleJobCostClick = () => {
    if (!selectedLead || !jobs || jobs.length === 0) {
      alert('No jobs found for this lead');
      return;
    }

    // Find most recent job by highest job number
    const mostRecentJob = jobs.reduce((latest, current) => {
      const latestNum = parseInt(latest.jobNumber, 10) || 0;
      const currentNum = parseInt(current.jobNumber, 10) || 0;
      return currentNum > latestNum ? current : latest;
    });

    navigate(`/jobs/${mostRecentJob.id}?tab=costs`);
  };

  const handleCloseConfirm = async (closeType: CloseType, reason: string) => {
    if (!selectedLeadId) return;

    setIsClosingLead(true);
    try {
      await transitionLeadStatus(selectedLeadId, closeType, reason);
      setIsCloseDialogOpen(false);
      refetch();
      loadLead(selectedLeadId);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to close lead');
    } finally {
      setIsClosingLead(false);
    }
  };

  // Icon button style - red icon color
  const iconButtonClass =
    'flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50';
  const iconClass = 'h-4 w-4 text-[#DC2626]';

  return (
    <div className="min-h-screen bg-white px-5 py-6 space-y-4">
      {/* Main Content Container */}
      <div className="bg-white rounded-[10px] p-5 space-y-4">
        {/* Lead Management Header - Title, Filters, Actions, Select Lead all in one row */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side: Title and Filter Links */}
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-[#0a0a9e]">Lead Management</h2>
            <LeadFilters
              onFilterChange={handleFilterChange}
              onAssignedToChange={handleAssignedToChange}
              currentFilter={currentFilter}
              users={users}
            />
          </div>

          {/* Right side: Action Buttons and Select Lead Dropdown */}
          <div className="flex items-center gap-2">
            {/* Action Buttons */}
            {canCreateLeads && (
              <Button
                onClick={handleNewLead}
                variant="outline"
                className={iconButtonClass}
              >
                <Plus className={iconClass} />
                New
              </Button>
            )}

            {canCreateLeads && (
              <Button
                onClick={handleCopyLead}
                disabled={!selectedLead}
                variant="outline"
                className={iconButtonClass}
              >
                <Copy className={iconClass} />
                Copy
              </Button>
            )}

            {canUpdateLeads && (
              <Button
                onClick={handleCloseClick}
                disabled={!selectedLead}
                variant="outline"
                className={iconButtonClass}
              >
                <X className={iconClass} />
                Close
              </Button>
            )}

            <Button
              disabled={!selectedLead || !jobs || jobs.length === 0}
              variant="outline"
              className={iconButtonClass}
              onClick={handleJobCostClick}
            >
              <DollarSign className={iconClass} />
              Job Cost
            </Button>

            <Button
              variant="outline"
              className={iconButtonClass}
              onClick={() => navigate('/leads/search')}
            >
              <Search className={iconClass} />
              Search
            </Button>

            {selectedLead && selectedLead.id > 0 ? (
              <PrintLeadSheetButton
                leadId={selectedLead.id}
                jobNumber={selectedLead.jobNumber}
                variant="outline"
                className={iconButtonClass}
                onError={(error) => alert(error)}
              />
            ) : (
              <Button
                disabled
                variant="outline"
                className={iconButtonClass}
              >
                <Printer className={iconClass} />
                Print
              </Button>
            )}

            {/* Select Lead Dropdown */}
            <div className="w-[200px]">
              <Select
                value={selectedLeadId?.toString() || ''}
                onChange={(e) => handleLeadSelect(e.target.value ? Number(e.target.value) : undefined)}
                className="h-9 rounded-md border border-[#d9d9d9] px-3 text-sm"
              >
                <option value="">Select Lead</option>
                {leads.map((lead) => (
                  <option key={lead.id} value={lead.id}>
                    {lead.leadTitle}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D71F20] mx-auto"></div>
          <p className="mt-3 text-[#84878A] text-sm">Loading leads...</p>
        </div>
      )}

      <ErrorAlert error={error} className="rounded-[20px] p-2" />

      {/* Lead Form - Inline Display */}
      {!isLoading && !error && (selectedLead !== null || selectedLeadId === undefined) && (
        <LeadForm
          lead={selectedLead}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
          readOnly={selectedLead && selectedLead.id > 0 ? !canUpdateLeads : !canCreateLeads}
        />
      )}

      {/* Jobs Section - Show jobs associated with this lead (one-to-many) */}
      {selectedLead && selectedLead.id > 0 && (
        <LeadJobsPanel leadId={selectedLead.id} />
      )}

      {/* Lead Notes Section */}
      {selectedLead && selectedLead.id > 0 && (
        <LeadNotesSection leadId={selectedLead.id} />
      )}

      {/* Assign Lead Modal */}
      {selectedLeadId && (
        <AssignLeadModal
          leadId={selectedLeadId}
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Close Lead Dialog */}
      <CloseLeadDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        closeType={selectedCloseType}
        onConfirm={handleCloseConfirm}
        isLoading={isClosingLead}
      />
    </div>
  );
}
